/**
 * Scoring, levels, combo and back-to-back.
 *
 * Everything here is a pure function of its arguments. The actual numbers live
 * in `TUNING` (see `core/game.ts`) and are passed in, so rule tweaks happen in
 * one place.
 */

export type TSpinKind = 'none' | 'mini' | 'full';

export interface ScoringTable {
  /** Points per line count (index 1..4), before the level multiplier. */
  lineClear: readonly [number, number, number, number, number];
  /** T-spin awards, before the level multiplier. */
  tspinMini: number;
  tspinZero: number;
  tspinSingle: number;
  tspinDouble: number;
  tspinTriple: number;
  /** Multiplier applied to consecutive "difficult" clears. */
  backToBackMultiplier: number;
  /** Points per combo step: value * combo * level. */
  comboUnit: number;
  softDropPerCell: number;
  hardDropPerCell: number;
}

export interface ClearContext {
  lines: number;
  tspin: TSpinKind;
  level: number;
  /** Combo counter AFTER this placement: 0 for the first clear in a chain. */
  combo: number;
  /** Whether the previous difficult clear chain is still alive. */
  backToBack: boolean;
}

export interface ClearScore {
  /** Points from the clear itself (includes the back-to-back multiplier). */
  base: number;
  /** Points from the combo chain. */
  combo: number;
  total: number;
  /** Whether this placement counted as "difficult" for back-to-back purposes. */
  difficult: boolean;
  /** Whether the back-to-back multiplier was applied. */
  backToBackApplied: boolean;
}

/** Tetris and any line-clearing T-spin keep a back-to-back chain alive. */
export function isDifficult(lines: number, tspin: TSpinKind): boolean {
  if (lines === 0) return false;
  return lines === 4 || tspin !== 'none';
}

export function scoreClear(ctx: ClearContext, table: ScoringTable): ClearScore {
  const { lines, tspin, level } = ctx;

  let base = 0;
  if (tspin === 'mini') {
    base = table.tspinMini;
  } else if (tspin === 'full') {
    base = [table.tspinZero, table.tspinSingle, table.tspinDouble, table.tspinTriple][
      Math.min(lines, 3)
    ];
  } else {
    base = table.lineClear[Math.min(lines, 4)];
  }
  base *= level;

  const difficult = isDifficult(lines, tspin);
  const backToBackApplied = difficult && ctx.backToBack;
  if (backToBackApplied) base = Math.floor(base * table.backToBackMultiplier);

  const combo = ctx.combo > 0 && lines > 0 ? table.comboUnit * ctx.combo * level : 0;

  return { base, combo, total: base + combo, difficult, backToBackApplied };
}

/** Level 1 at 0 lines, then one level per `linesPerLevel` cleared. */
export function levelForLines(lines: number, linesPerLevel: number): number {
  return Math.floor(lines / linesPerLevel) + 1;
}

/**
 * Guideline gravity curve: seconds per row = (0.8 - (level-1)*0.007)^(level-1).
 * The base is clamped so absurd levels stay finite and positive.
 */
export function gravityIntervalMs(level: number): number {
  const n = Math.max(0, level - 1);
  const base = Math.max(0.0001, 0.8 - n * 0.007);
  return base ** n * 1000;
}
